import { useCallback, useEffect, useRef, useState } from 'react';

export const HISTORY_PANEL_OPEN_MS = 280;
export const HISTORY_PANEL_CLOSE_MS = 220;
export const DETAIL_PANEL_OPEN_MS = 380;
export const DETAIL_PANEL_CLOSE_MS = 300;
export const DETAIL_PANEL_SWITCH_GAP_MS = 72;

/**
 * 历史侧栏进出场：动画期间 defer 重内容，避免与 slide 动画争抢主线程。
 *
 * @param {boolean} open - 父组件控制的开关
 * @param {() => void} [onClose] - 关闭回调
 * @param {{ openMs?: number; deferContent?: boolean; closeNotifiesParent?: boolean; baseClassName?: string; onExitComplete?: () => void }} [options]
 *   closeNotifiesParent=true 时 requestClose 立即通知父组件（open=false 触发退出）；
 *   false 时本地 exiting，动画结束后再 onClose。
 */
export function useHistoryPanelTransition(open, onClose, options = {}) {
  const {
    openMs = HISTORY_PANEL_OPEN_MS,
    deferContent = true,
    closeNotifiesParent = true,
    baseClassName = 'img2img-history',
    onExitComplete,
  } = options;

  const [mounted, setMounted] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const deferTimerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onExitCompleteRef = useRef(onExitComplete);
  onCloseRef.current = onClose;
  onExitCompleteRef.current = onExitComplete;

  useEffect(() => {
    if (open) {
      setMounted(true);
      setExiting(false);
      if (!deferContent) {
        setContentReady(true);
      } else {
        setContentReady(false);
      }
    } else if (mounted) {
      setExiting(true);
      setContentReady(false);
      clearTimeout(deferTimerRef.current);
    }
    return () => clearTimeout(deferTimerRef.current);
  }, [open, mounted, deferContent]);

  // prefers-reduced-motion 等场景下 animationend 可能不触发，用 timeout 兜底
  useEffect(() => {
    if (!mounted || exiting || contentReady || !deferContent || !open) return undefined;
    deferTimerRef.current = setTimeout(() => setContentReady(true), openMs + 80);
    return () => clearTimeout(deferTimerRef.current);
  }, [mounted, exiting, contentReady, deferContent, open, openMs]);

  const handleExitComplete = useCallback(() => {
    setMounted(false);
    setExiting(false);
    onExitCompleteRef.current?.();
  }, []);

  const handlePanelAnimationEnd = useCallback((event) => {
    if (event.target !== event.currentTarget) return;
    if (exiting) {
      handleExitComplete();
      if (!closeNotifiesParent) {
        onCloseRef.current?.();
      }
      return;
    }
    if (deferContent && !contentReady) {
      clearTimeout(deferTimerRef.current);
      setContentReady(true);
    }
  }, [exiting, handleExitComplete, closeNotifiesParent, deferContent, contentReady]);

  const requestClose = useCallback(() => {
    if (exiting) return;
    if (closeNotifiesParent) {
      onCloseRef.current?.();
    } else {
      setExiting(true);
    }
  }, [exiting, closeNotifiesParent]);

  const historyClassName = [
    baseClassName,
    exiting ? 'is-closing' : 'is-opening',
    contentReady ? 'is-content-ready' : '',
  ].filter(Boolean).join(' ');

  return {
    mounted,
    exiting,
    contentReady,
    historyClassName,
    requestClose,
    handlePanelAnimationEnd,
  };
}
